import { forwardRef } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import type { IconButtonProps } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { AriaAttributes } from 'react';

type ActionIconButtonProps = {
  label: string;
  icon: React.ReactNode;
  tooltip?: string;
  variant?: 'contained' | 'outlined' | 'text';
  color?: IconButtonProps['color'];
  disabled?: boolean;
  onClick?: IconButtonProps['onClick'];
  'aria-haspopup'?: AriaAttributes['aria-haspopup'];
  'aria-expanded'?: boolean;
  'aria-controls'?: string;
  sx?: SxProps<Theme>;
  size?: IconButtonProps['size'];
};

type ActionIconButtonColor = NonNullable<IconButtonProps['color']>;
type ActionPaletteColor = {
  main?: string;
  dark?: string;
  contrastText?: string;
};

const getPaletteColor = (color: ActionIconButtonColor) =>
  color === 'default' || color === 'inherit' ? 'neutral' : color;

const resolvePaletteColor = (theme: Theme, color: ActionIconButtonColor): ActionPaletteColor =>
  (theme.palette[getPaletteColor(color) as keyof Theme['palette']] as ActionPaletteColor | undefined) ??
  theme.palette.primary;

const getOutlinedSx = (color: ActionIconButtonColor): SxProps<Theme> => ({
  border: 1,
  borderColor: theme => resolvePaletteColor(theme, color).main ?? theme.palette.primary.main,
  borderRadius: 1,
  p: 0.5,
  '&.Mui-disabled': {
    borderColor: 'border.default',
  },
});

const getContainedSx = (color: ActionIconButtonColor): SxProps<Theme> => ({
  backgroundColor: theme => resolvePaletteColor(theme, color).main ?? theme.palette.primary.main,
  borderRadius: 1,
  color: theme => {
    const paletteColor = resolvePaletteColor(theme, color);
    const mainColor = paletteColor.main ?? theme.palette.primary.main;
    return paletteColor.contrastText ?? theme.palette.getContrastText(mainColor);
  },
  p: 0.625,
  '&:hover': {
    backgroundColor: theme => {
      const paletteColor = resolvePaletteColor(theme, color);
      return paletteColor.dark ?? paletteColor.main ?? theme.palette.primary.dark;
    },
  },
});

const getIconSx = (size: IconButtonProps['size']): SxProps<Theme> => ({
  '& svg': {
    fontSize: size === 'small' ? 16 : size === 'large' ? 28 : 20,
  },
});

const ActionIconButton = forwardRef<HTMLButtonElement, ActionIconButtonProps>(
  (
    {
      label,
      icon,
      tooltip,
      variant = 'contained',
      color = 'primary',
      disabled,
      onClick,
      'aria-haspopup': ariaHasPopup,
      'aria-expanded': ariaExpanded,
      'aria-controls': ariaControls,
      sx,
      size = 'small',
    },
    ref,
  ) => {
    const variantSx =
      variant === 'outlined' ? getOutlinedSx(color) : variant === 'contained' ? getContainedSx(color) : undefined;

    const button = (
      <IconButton
        ref={ref}
        aria-label={label}
        color={color}
        disabled={disabled}
        onClick={onClick}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        size={size}
        sx={[...(variantSx ? [variantSx] : []), getIconSx(size), ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      >
        {icon}
      </IconButton>
    );

    return tooltip ? (
      <Tooltip title={tooltip}>
        <span>{button}</span>
      </Tooltip>
    ) : (
      button
    );
  },
);

ActionIconButton.displayName = 'ActionIconButton';

export default ActionIconButton;
